import type {
  XtDepthResponse,
  XtKlinesResponse,
  XtSymbolsResponse,
  XtTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://sapi.xt.com';

/**
 * XT.com REST API endpoints.
 * @see https://doc.xt.com/
 */
export const xtRestEndpoints = {
  ticker24h: `${BASE_URL}/v4/public/ticker/24h`,
  ticker: `${BASE_URL}/v4/public/ticker`,
  depth: `${BASE_URL}/v4/public/depth`,
  klines: `${BASE_URL}/v4/public/kline`,
  /** Recent trades - simplified endpoint without pagination */
  recentTrades: `${BASE_URL}/v4/public/trade/recent`,
  /** Historical trades - requires pagination with direction param */
  trades: `${BASE_URL}/v4/public/trade/history`,
  symbols: `${BASE_URL}/v4/public/symbol`,
} as const;

// ========================================
// Helper Functions
// ========================================

/**
 * Format symbol for XT API calls.
 * XT uses lowercase underscore format: "btc_usdt", "eth_usdt".
 *
 * @example
 * formatXtSymbol("BTC", "USDT") // "btc_usdt"
 */
export function formatXtSymbol(base: string, quote: string): string {
  return `${base}_${quote}`.toLowerCase();
}

/**
 * Parse XT symbol into base/quote.
 * XT symbols use underscore separator.
 *
 * @example
 * parseXtSymbol("btc_usdt") // { base: "BTC", quote: "USDT" }
 */
export function parseXtSymbol(symbol: string): { base: string; quote: string } | null {
  const parts = symbol.split('_');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null;
  }
  return {
    base: parts[0].toUpperCase(),
    quote: parts[1].toUpperCase(),
  };
}

/**
 * Validate XT REST API response.
 * Throws error if response code (rc) is not "0".
 * Note: API may return rc as string "0" or number 0
 */
export function validateXtResponse<T extends { rc: string | number; mc: string }>(
  response: T,
  operation: string,
): void {
  // Handle both string "0" and number 0
  if (response.rc != 0) {
    throw new Error(`XT ${operation} failed: rc=${response.rc}, mc=${response.mc}`);
  }
}

export interface XtTicker24hRequest {
  /** Trading pair symbol (e.g., btc_usdt) */
  symbol: string;
}

export interface XtTicker24hResponse {
  rc: number;
  mc: string;
  ma: unknown[];
  result: {
    /** Trading pair symbol */
    s: string;
    /** Last traded price (close) */
    c: string;
    /** 24h high price */
    h: string;
    /** 24h low price */
    l: string;
    /** 24h open price */
    o: string;
    /** Transaction quantity (base volume) */
    q: string;
    /** Transaction volume (quote volume) */
    v: string;
    /** Price change rate */
    cr: string;
    /** Price change value */
    cv: string;
    [key: string]: unknown;
  }[];
}

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://doc.xt.com/docs/spot/Market/Get24hStatisticsTicker
 */
export const getXtTicker24h = async (
  params: XtTicker24hRequest,
  options?: RestRequestOptions,
): Promise<XtTicker24hResponse> => {
  const response = await get<XtTicker24hResponse, XtTicker24hRequest>({
    url: xtRestEndpoints.ticker24h,
    params,
    options,
  });

  return response.data;
};

export interface XtTickerRequest {
  /** Trading pair symbol (e.g., btc_usdt) */
  symbol: string;
}

export interface XtTickerResponse {
  rc: number;
  mc: string;
  ma: unknown[];
  result: {
    /** Trading pair symbol */
    s: string;
    /** Last traded price (close) */
    c: string;
    /** Best bid price */
    bp: string;
    /** Best ask price */
    ap: string;
    /** 24h high price */
    h: string;
    /** 24h low price */
    l: string;
    /** 24h open price */
    o: string;
    /** Transaction quantity (base volume) */
    v: string;
    /** Transaction volume (quote volume) */
    q: string;
    /** Price change rate */
    cr: string;
    /** Price change value */
    cv: string;
    [key: string]: unknown;
  }[];
}

/**
 * Fetches current ticker data including bid/ask prices for a symbol.
 * @see https://doc.xt.com/docs/spot/Market/GetTicker
 */
export const getXtTicker = async (
  params: XtTickerRequest,
  options?: RestRequestOptions,
): Promise<XtTickerResponse> => {
  const response = await get<XtTickerResponse, XtTickerRequest>({
    url: xtRestEndpoints.ticker,
    params,
    options,
  });

  return response.data;
};

// ========================================
// Depth (Orderbook) Endpoint
// ========================================

/**
 * Request parameters for getXtDepth.
 */
export interface XtDepthRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Depth limit: 1-500, default 100 */
  limit?: number;
}

/**
 * Fetches orderbook depth (snapshot) for a symbol.
 * Returns bids/asks as [price, quantity] tuples with sequence ID for delta tracking.
 *
 * @see https://doc.xt.com/docs/spot/Market/GetDepthData
 */
export const getXtDepth = async (
  params: XtDepthRequest,
  options?: RestRequestOptions,
): Promise<XtDepthResponse> => {
  const response = await get<XtDepthResponse>({
    url: xtRestEndpoints.depth,
    params: {
      symbol: params.symbol,
      limit: params.limit ?? 100,
    },
    options,
  });

  validateXtResponse(response.data, 'getXtDepth');

  return response.data;
};

// ========================================
// Klines (Candlesticks) Endpoint
// ========================================

/**
 * XT supported kline intervals.
 * @see https://doc.xt.com/docs/spot/Market/GetKlineData
 */
export type XtKlineInterval =
  | '1m'
  | '3m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '6h'
  | '8h'
  | '12h'
  | '1d'
  | '3d'
  | '1w'
  | '1M';

/**
 * Request parameters for getXtKlines.
 */
export interface XtKlinesRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Kline interval */
  interval: XtKlineInterval;
  /** Start time (Unix timestamp in milliseconds) */
  startTime?: number;
  /** End time (Unix timestamp in milliseconds) */
  endTime?: number;
  /** Number of candles to return: 1-1000 */
  limit?: number;
}

/**
 * Fetches historical klines (candlesticks) from XT.
 * Returns candles with open/high/low/close prices and volume.
 *
 * @see https://doc.xt.com/docs/spot/Market/GetKlineData
 */
export const getXtKlines = async (
  params: XtKlinesRequest,
  options?: RestRequestOptions,
): Promise<XtKlinesResponse> => {
  const queryParams: Record<string, string | number> = {
    symbol: params.symbol,
    interval: params.interval,
  };

  if (params.startTime !== undefined) queryParams.startTime = params.startTime;
  if (params.endTime !== undefined) queryParams.endTime = params.endTime;
  if (params.limit !== undefined) queryParams.limit = params.limit;

  const response = await get<XtKlinesResponse>({
    url: xtRestEndpoints.klines,
    params: queryParams,
    options,
  });

  validateXtResponse(response.data, 'getXtKlines');

  return response.data;
};

// ========================================
// Recent Trades Endpoint (Recommended for snapshots)
// ========================================

/**
 * Request parameters for getXtRecentTrades.
 * @see https://doc.xt.com/docs/spot/Market/QueryRecentTransactions
 */
export interface XtRecentTradesRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Number of trades to return: 1-1000, default 200 */
  limit?: number;
}

/**
 * Fetches recent public trades for a symbol using the simplified recent trades endpoint.
 * This endpoint is recommended for fetching trade snapshots as it doesn't require pagination.
 * Returns trades with price, quantity, timestamp, and side.
 *
 * @see https://doc.xt.com/docs/spot/Market/QueryRecentTransactions
 */
export const getXtRecentTrades = async (
  params: XtRecentTradesRequest,
  options?: RestRequestOptions,
): Promise<XtTradesResponse> => {
  const queryParams: Record<string, string | number> = {
    symbol: params.symbol,
  };

  if (params.limit !== undefined) queryParams.limit = params.limit;

  const response = await get<XtTradesResponse>({
    url: xtRestEndpoints.recentTrades,
    params: queryParams,
    options,
  });

  validateXtResponse(response.data, 'getXtRecentTrades');

  return response.data;
};

// ========================================
// Historical Trades Endpoint (For pagination)
// ========================================

/**
 * Request parameters for getXtTrades (historical with pagination).
 * @see https://doc.xt.com/docs/spot/Market/QueryHistoricalTransactions
 */
export interface XtTradesRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Number of trades to return: 1-1000, default 200 */
  limit?: number;
  /** Direction for pagination: "PREV" or "NEXT" (required for this endpoint) */
  direction: 'PREV' | 'NEXT';
  /** Trade ID to paginate from */
  fromId?: number;
}

/**
 * Fetches historical public trades for a symbol with pagination support.
 * Use getXtRecentTrades for simple snapshots without pagination.
 * Returns trades with price, quantity, timestamp, and side.
 *
 * @see https://doc.xt.com/docs/spot/Market/QueryHistoricalTransactions
 */
export const getXtTrades = async (
  params: XtTradesRequest,
  options?: RestRequestOptions,
): Promise<XtTradesResponse> => {
  const queryParams: Record<string, string | number> = {
    symbol: params.symbol,
    direction: params.direction,
  };

  if (params.limit !== undefined) queryParams.limit = params.limit;
  if (params.fromId !== undefined) queryParams.fromId = params.fromId;

  const response = await get<XtTradesResponse>({
    url: xtRestEndpoints.trades,
    params: queryParams,
    options,
  });

  validateXtResponse(response.data, 'getXtTrades');

  return response.data;
};

// ========================================
// Symbols (Instrument Information) Endpoint
// ========================================

/**
 * Request parameters for getXtSymbols.
 */
export interface XtSymbolsRequest {
  /** Single symbol to query (optional) */
  symbol?: string;
  /** Comma-separated list of symbols (optional) */
  symbols?: string;
  /** Data version for caching (optional) */
  version?: string;
}

/**
 * Fetches symbol information and trading rules.
 * Returns precision, trading status, and other metadata.
 *
 * @see https://doc.xt.com/docs/spot/Market/GetSymbolInformation
 */
export const getXtSymbols = async (
  params?: XtSymbolsRequest,
  options?: RestRequestOptions,
): Promise<XtSymbolsResponse> => {
  const queryParams: Record<string, string> = {};

  if (params?.symbol) queryParams.symbol = params.symbol;
  if (params?.symbols) queryParams.symbols = params.symbols;
  if (params?.version) queryParams.version = params.version;

  const response = await get<XtSymbolsResponse>({
    url: xtRestEndpoints.symbols,
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    options,
  });

  validateXtResponse(response.data, 'getXtSymbols');

  return response.data;
};
